import { createRootRoute, Outlet, useRouter } from "@tanstack/react-router";
import { Button, Result } from "antd";
import { useEffect } from "react";
import { ArtemisAuthProvider } from "@/auth/artemis-auth-provider";
import { captureException } from "@/common/sentry";
import { ForbiddenError } from "@/common/use-gql-client";

function NotFound() {
	const router = useRouter();
	return (
		<Result
			status="404"
			title="404"
			subTitle="This page doesn't exist."
			extra={
				<Button type="primary" onClick={() => router.history.back()}>
					Go back
				</Button>
			}
			style={{ marginTop: 80 }}
		/>
	);
}

function AccessDenied() {
	const router = useRouter();
	return (
		<Result
			status="403"
			title="Access Denied"
			subTitle="You don't have permission to view this page."
			extra={
				<Button type="primary" onClick={() => router.history.back()}>
					Go back
				</Button>
			}
			style={{ marginTop: 80 }}
		/>
	);
}

function RootError({ error, reset }: { error: unknown; reset: () => void }) {
	const isForbidden = error instanceof ForbiddenError;
	// Route render/loader crashes land here instead of window.onerror, so Sentry's
	// global handlers never see them — forward explicitly. No-op unless Sentry.init
	// ran (prod only). Forbidden is an expected auth state, not an error.
	useEffect(() => {
		if (!isForbidden) captureException(error);
	}, [error, isForbidden]);
	if (isForbidden) return <AccessDenied />;
	const message =
		error instanceof Error ? error.message : "An unexpected error occurred.";
	return (
		<Result
			status="error"
			title="Something went wrong"
			subTitle={message}
			extra={
				<Button type="primary" onClick={reset}>
					Try again
				</Button>
			}
			style={{ marginTop: 80 }}
		/>
	);
}

export const Route = createRootRoute({
	component: () => (
		<ArtemisAuthProvider>
			<Outlet />
		</ArtemisAuthProvider>
	),
	notFoundComponent: NotFound,
	errorComponent: RootError,
});
